import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, CornerUpLeft, Lock, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { EditedMark, Field, FormSheet, HistoryList, RefSelect, RefsGate, SelectField, SheetSection, TimeStatusPill, inputClass, useDriftRefs } from '@/components/app/DriftUI';
import { TIME_STATUS_HELP, createTimeEntry, deleteTimeEntry, hoursText, isActiveRef, patchTimeEntry, updateTimeEntry } from '@/lib/driftApi';
import { NbDateInput, TimeInput } from '@/components/app/DateTimeFields';
import { formatDateShort, formatDateTime, formatDateWeekday, isValidDate, normalizeTime } from '@/domain/time';
import { fmtNumber } from '@/domain/numbers';
import { cn } from '@/lib/utils';

/*
  Felles timeføring for «Min dag» (ansatt) og «Timer» (kontor). Samme poster, samme skjema, samme historikk.
  Vanlige felt først (dato, tid, kunde → prosjekt, aktivitet, kommentar); lønnsart/tillegg og maskin åpnes ved behov.
  Status «utkast / sendt / returnert / godkjent» er en forenklet eksempelstatus – ikke kundens lønns- eller godkjenningsregler.
  Kontekst «employee»: medarbeideren er demoprofilen (ikke valgbar i skjemaet). Kontekst «office»: medarbeider velges, og
  kontoret kan godkjenne / returnere med begrunnelse / gjenåpne.
*/

export const emptyTimeForm = (today, employeeId, projectId = '', subprojectId = '', customerId = '') => ({
  date: today,
  employeeId: employeeId ?? '',
  customerId,
  projectId,
  subprojectId,
  activityId: '',
  fromTime: '07:00',
  toTime: '15:00',
  breakMinutes: '30',
  wageTypeId: 'ordinaer',
  supplements: [],
  comment: '',
  machineId: '',
  machineHours: '',
  machineMinutes: '',
  reason: '',
});

/** Skjemaverdier fra en lagret føring (redigering). */
export const timeFormFromEntry = (e) => ({
  date: e.date,
  employeeId: e.employeeId ?? '',
  customerId: e.project?.customerId ?? '',
  projectId: e.projectId ?? '',
  subprojectId: e.subprojectId ?? '',
  activityId: e.activityId ?? '',
  fromTime: e.fromTime,
  toTime: e.toTime,
  breakMinutes: String(e.breakMinutes ?? 0),
  wageTypeId: e.wageTypeId ?? 'ordinaer',
  supplements: (e.supplements ?? []).map((s) => ({ id: s.id, quantity: String(s.quantity) })),
  comment: e.comment ?? '',
  machineId: e.machineId ?? '',
  machineHours: e.machineHours != null ? String(e.machineHours) : '',
  machineMinutes: e.machineMinutes != null ? String(e.machineMinutes) : '',
  reason: '',
});

/** «Samme som sist» / kopier: alt fra forrige føring, men ny dato og tom kommentar – alltid redigerbart før det sendes. */
export const timeFormFromTemplate = (e, today, employeeId) => ({ ...timeFormFromEntry(e), date: today, employeeId: employeeId ?? e.employeeId ?? '', comment: '' });

export const timeFormToBody = (form) => ({
  date: form.date,
  employeeId: form.employeeId,
  projectId: form.projectId,
  subprojectId: form.subprojectId || null,
  activityId: form.activityId,
  fromTime: normalizeTime(form.fromTime) ?? form.fromTime,
  toTime: normalizeTime(form.toTime) ?? form.toTime,
  breakMinutes: Number(form.breakMinutes) || 0,
  wageTypeId: form.wageTypeId,
  supplements: form.supplements.map((s) => ({ id: s.id, quantity: Number(s.quantity) })),
  comment: form.comment || null,
  machineId: form.machineId || null,
  machineHours: form.machineId ? Number(form.machineHours) || 0 : null,
  machineMinutes: form.machineId ? Number(form.machineMinutes) || 0 : null,
});

export function netHours(f) {
  const from = normalizeTime(f.fromTime);
  const to = normalizeTime(f.toTime);
  if (!from || !to) return null;
  const [fh, fm] = from.split(':').map(Number);
  const [th, tm] = to.split(':').map(Number);
  const net = th * 60 + tm - (fh * 60 + fm) - (Number(f.breakMinutes) || 0);
  return net > 0 ? net / 60 : null;
}

export function validateTimeForm(f) {
  const e = {};
  if (!f.date || !isValidDate(f.date)) e.date = 'Velg dato (dd.mm.åååå)';
  if (!f.employeeId) e.employeeId = 'Velg medarbeider';
  if (!f.projectId) e.projectId = 'Velg kunde og prosjekt';
  if (!f.activityId) e.activityId = 'Velg aktivitet';
  if (!normalizeTime(f.fromTime)) e.fromTime = 'Skriv klokkeslett som 07:00';
  if (!normalizeTime(f.toTime)) e.toTime = 'Skriv klokkeslett som 15:00';
  const h = netHours(f);
  if (h === null && !e.fromTime && !e.toTime) e.toTime = 'Til-tid må være etter fra-tid, og pausen kortere enn arbeidstiden';
  if (f.machineId && !(Number(f.machineHours) > 0 || Number(f.machineMinutes) > 0)) e.machineHours = 'Oppgi timer og/eller minutter på maskinen';
  f.supplements.forEach((s) => {
    if (!(Number(s.quantity) > 0)) e.supplements = 'Oppgi mengde på hvert tillegg';
  });
  return e;
}

/** Sammendragsrad som åpner/lukker en detaljseksjon (tillegg, maskin). Stor trykkflate, tydelig tilstand. */
function DisclosureRow({ open, onOpenChange, label, summary, active, children, testId }) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className={cn('rounded-[12px] border', open ? 'border-[var(--hr-border)]' : 'border-[var(--hr-hairline)]')} data-testid={testId}>
      <CollapsibleTrigger asChild>
        <button type="button" className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-[12px] px-3.5 py-2.5 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" aria-expanded={open} data-testid={`${testId}-toggle`}>
          <span className="min-w-0">
            <span className="block text-[14px] font-semibold text-[var(--hr-heading)]">{label}</span>
            <span className={cn('block truncate text-[13px]', active ? 'text-[var(--hr-text)]' : 'text-[var(--hr-muted-2)]')} data-testid={`${testId}-summary`}>
              {summary}
            </span>
          </span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-[var(--hr-muted-2)] transition-transform duration-200', open && 'rotate-180')} aria-hidden="true" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-[var(--hr-hairline)] px-3.5 pb-4 pt-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

export function TimeEntryForm({ refs, form, setForm, errors, editing, context = 'employee', locked = false, formKey = '' }) {
  const project = refs.projects.find((p) => p.id === form.projectId);
  // Kunde eksplisitt: aktive kunder + kunden på føringen. Prosjektlisten begrenses til valgt kunde.
  const customers = refs.customers ?? [];
  const customerOptions = customers.filter((c) => isActiveRef(c) || c.id === form.customerId).map((c) => ({ value: c.id, label: c.name, inactive: !isActiveRef(c), hint: isActiveRef(c) ? undefined : 'arkivert' }));
  const projects = form.customerId ? refs.projects.filter((p) => p.customerId === form.customerId || p.id === form.projectId) : refs.projects;
  // Underprosjekt er alltid begrenset til valgt prosjekt. Arkiverte underprosjekter vises bare når de allerede står på føringen.
  const subprojects = (project?.subprojects ?? []).filter((s) => isActiveRef(s) || s.id === form.subprojectId);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const hours = netHours(form);
  const hasExtra = form.supplements.length > 0 || (form.wageTypeId && form.wageTypeId !== 'ordinaer');
  const [extraOpen, setExtraOpen] = useState(hasExtra);
  const [machineOpen, setMachineOpen] = useState(Boolean(form.machineId));
  useEffect(() => {
    // Når panelet åpnes for en annen føring / ny registrering, følger seksjonene innholdet (åpne når de har verdier).
    setExtraOpen(form.supplements.length > 0 || (form.wageTypeId && form.wageTypeId !== 'ordinaer'));
    setMachineOpen(Boolean(form.machineId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const toggleSupplement = (id) =>
    setForm((f) => (f.supplements.some((s) => s.id === id) ? { ...f, supplements: f.supplements.filter((s) => s.id !== id) } : { ...f, supplements: [...f.supplements, { id, quantity: '' }] }));
  const setQty = (id, q) => setForm((f) => ({ ...f, supplements: f.supplements.map((s) => (s.id === id ? { ...s, quantity: q } : s)) }));

  const wage = refs.wageTypes.find((w) => w.id === form.wageTypeId);
  const extraSummary = [wage?.label ?? 'Ordinær tid', form.supplements.length ? `${form.supplements.length} ${form.supplements.length === 1 ? 'tillegg' : 'tillegg'}` : 'ingen tillegg'].join(' · ');
  const machine = refs.machines.find((m) => m.machineId === form.machineId);
  const machineTotal = form.machineId ? (Number(form.machineHours) || 0) + (Number(form.machineMinutes) || 0) / 60 : 0;
  const machineSummary = machine ? `${machine.internalNo} · ${machine.name}${machineTotal > 0 ? ` · ${hoursText(machineTotal)}` : ''}` : 'Ingen maskin';
  const dis = locked;

  return (
    <div className="space-y-4" aria-disabled={dis || undefined}>
      <div className={cn('grid gap-3', context === 'office' ? 'grid-cols-2' : 'grid-cols-1')}>
        <Field label="Dato" htmlFor="t-date" required error={errors.date} hint={form.date && isValidDate(form.date) ? formatDateWeekday(form.date) : 'dd.mm.åååå'}>
          <NbDateInput id="t-date" value={form.date} onChange={set('date')} disabled={dis} testId="timer-date" />
        </Field>
        {context === 'office' && (
          <Field label="Medarbeider" htmlFor="t-emp" required error={errors.employeeId}>
            <RefSelect id="t-emp" value={form.employeeId} onValueChange={set('employeeId')} list={refs.employees} getHint={(x) => x.role} disabled={dis} testId="timer-employee" />
          </Field>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Fra" htmlFor="t-from" required error={errors.fromTime}>
          <TimeInput id="t-from" value={form.fromTime} onChange={set('fromTime')} disabled={dis} ariaLabel="Fra klokkeslett" placeholder="07:00" testId="timer-from" />
        </Field>
        <Field label="Til" htmlFor="t-to" required error={errors.toTime && errors.toTime.startsWith('Skriv') ? errors.toTime : undefined}>
          <TimeInput id="t-to" value={form.toTime} onChange={set('toTime')} disabled={dis} ariaLabel="Til klokkeslett" placeholder="15:00" testId="timer-to" />
        </Field>
        <Field label="Pause (min)" htmlFor="t-break">
          <Input id="t-break" type="number" inputMode="numeric" min={0} max={480} step={5} value={form.breakMinutes} onChange={(e) => set('breakMinutes')(e.target.value)} disabled={dis} className={inputClass} data-testid="timer-break" />
        </Field>
      </div>
      <div className={cn('flex items-center justify-between gap-3 rounded-[10px] px-3 py-2 text-[14px]', hours === null ? 'bg-[var(--hr-red-bg)] text-[var(--hr-red)]' : 'bg-[var(--hr-surface-2)] text-[var(--hr-text)]')} data-testid="timer-net-hours">
        <span className="hr-tabular min-w-0">{hours === null ? 'Arbeidstid' : `Arbeidstid ${normalizeTime(form.fromTime)}–${normalizeTime(form.toTime)}${Number(form.breakMinutes) > 0 ? ` · pause ${Number(form.breakMinutes)} min` : ''}`}</span>
        <span className="hr-num shrink-0 text-right font-semibold">{hours === null ? (errors.toTime && !errors.toTime.startsWith('Skriv') ? errors.toTime : 'Kontroller fra/til og pause') : hoursText(hours)}</span>
      </div>

      <Field label="Kunde" htmlFor="t-cust" required error={!form.customerId && errors.projectId ? errors.projectId : undefined}>
        <SelectField
          id="t-cust"
          value={form.customerId}
          onValueChange={(v) => setForm((f) => ({ ...f, customerId: v, ...(refs.projects.find((p) => p.id === f.projectId)?.customerId === v ? {} : { projectId: '', subprojectId: '' }) }))}
          options={customerOptions}
          disabled={dis}
          placeholder="Velg kunde"
          testId="timer-customer"
        />
      </Field>
      <Field label="Prosjekt" htmlFor="t-proj" required error={form.customerId ? errors.projectId : undefined} hint={project?.status === 'avsluttet' ? 'Avsluttet prosjekt – beholdes kun på eksisterende føring' : undefined}>
        <RefSelect
          id="t-proj"
          value={form.projectId}
          onValueChange={(v) => setForm((f) => ({ ...f, projectId: v, subprojectId: '', customerId: refs.projects.find((p) => p.id === v)?.customerId ?? f.customerId }))}
          list={projects}
          getHint={(p) => (form.customerId ? p.location : p.customer)}
          disabled={dis}
          placeholder={form.customerId ? 'Velg prosjekt' : 'Velg kunde først, eller prosjekt direkte'}
          testId="timer-project"
        />
      </Field>
      {project && (
        <p className="-mt-1 text-[13px] text-[var(--hr-muted)]" data-testid="timer-selected-customer-project">
          Valgt: <span className="font-medium text-[var(--hr-text)]">{project.customer}</span> – {project.name}
        </p>
      )}
      <div className={cn('grid gap-3', subprojects.length > 0 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}>
        {subprojects.length > 0 && (
          <Field label="Underprosjekt" htmlFor="t-sub">
            <SelectField id="t-sub" value={form.subprojectId} onValueChange={set('subprojectId')} allowEmpty emptyLabel="Ingen underprosjekt" options={subprojects.map((s) => ({ value: s.id, label: s.name }))} disabled={dis} testId="timer-subproject" />
          </Field>
        )}
        <Field label="Aktivitet" htmlFor="t-act" required error={errors.activityId}>
          <RefSelect id="t-act" value={form.activityId} onValueChange={set('activityId')} list={refs.activities} disabled={dis} testId="timer-activity" />
        </Field>
      </div>

      <Field label="Kommentar" htmlFor="t-comment" hint={wage?.requiresComment || refs.supplements.some((s) => s.requiresComment && form.supplements.some((x) => x.id === s.id)) ? 'Valgt lønnsart/tillegg krever en kort kommentar' : undefined}>
        <Textarea id="t-comment" value={form.comment} onChange={(e) => set('comment')(e.target.value)} rows={2} maxLength={1000} placeholder="Hva ble gjort?" disabled={dis} className="min-h-[64px] rounded-[10px] border-[var(--hr-border)] text-[16px] shadow-none lg:text-[14px]" data-testid="timer-comment" />
      </Field>

      <DisclosureRow open={extraOpen} onOpenChange={setExtraOpen} label="Lønnsart og tillegg" summary={extraSummary} active={form.supplements.length > 0 || form.wageTypeId !== 'ordinaer'} testId="timer-extra">
        <div className="space-y-4">
          <Field label="Lønnsart (kategori)" htmlFor="t-wage" hint="Kun kategori – ingen satser i eksempeldataene">
            <RefSelect id="t-wage" value={form.wageTypeId} onValueChange={set('wageTypeId')} list={refs.wageTypes} getHint={(w) => (w.requiresComment ? 'krever kommentar' : undefined)} disabled={dis} testId="timer-wage" />
          </Field>
          <div>
            <div className="mb-1.5 text-[13px] font-medium text-[var(--hr-text)]">Tillegg (mengde)</div>
            <div className="flex flex-wrap gap-2">
              {refs.supplements.filter((s) => isActiveRef(s) || form.supplements.some((x) => x.id === s.id)).map((s) => {
                const on = form.supplements.some((x) => x.id === s.id);
                return (
                  <button key={s.id} type="button" onClick={() => toggleSupplement(s.id)} disabled={dis} aria-pressed={on} className={cn('h-10 rounded-full border px-3.5 text-[14px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] disabled:opacity-60', on ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]')} data-testid={`timer-supplement-${s.id}`}>
                    {s.label} <span className={cn('text-[12px]', on ? 'text-white/70' : 'text-[var(--hr-muted-2)]')}>({s.unit})</span>
                  </button>
                );
              })}
            </div>
            {form.supplements.length > 0 && (
              <div className="mt-3 space-y-2">
                {form.supplements.map((s) => {
                  const def = refs.supplements.find((x) => x.id === s.id);
                  return (
                    <div key={s.id} className="flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate text-[14px] text-[var(--hr-text)]">{def?.label}</span>
                      <Input type="number" inputMode="decimal" min={0} step={def?.unit === 'stk' ? 1 : 0.5} value={s.quantity} onChange={(e) => setQty(s.id, e.target.value)} placeholder="0" disabled={dis} aria-label={`Mengde ${def?.label}`} className={cn(inputClass, 'w-24 text-right')} data-testid={`timer-supplement-qty-${s.id}`} />
                      <span className="w-8 text-[13px] text-[var(--hr-muted)]">{def?.unit}</span>
                    </div>
                  );
                })}
                {errors.supplements && <p className="text-[13px] text-[var(--hr-red)]" role="alert">{errors.supplements}</p>}
              </div>
            )}
          </div>
        </div>
      </DisclosureRow>

      <DisclosureRow open={machineOpen} onOpenChange={setMachineOpen} label="Maskin" summary={machineSummary} active={Boolean(form.machineId)} testId="timer-machine-section">
        <div className="space-y-3">
          <Field label="Maskin" htmlFor="t-machine" hint="Maskintimer føres på maskinen og følger samme kunde og prosjekt som timene.">
            <RefSelect id="t-machine" value={form.machineId} onValueChange={(v) => setForm((f) => ({ ...f, machineId: v, machineHours: v ? f.machineHours : '', machineMinutes: v ? f.machineMinutes : '' }))} allowEmpty emptyLabel="Ingen maskin" list={refs.machines} idKey="machineId" getLabel={(m) => `${m.internalNo} · ${m.name}`} disabled={dis} testId="timer-machine" />
          </Field>
          {form.machineId && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Maskintimer" htmlFor="t-mh" error={errors.machineHours}>
                <Input id="t-mh" type="number" inputMode="numeric" min={0} max={24} value={form.machineHours} onChange={(e) => set('machineHours')(e.target.value)} placeholder="0" disabled={dis} className={inputClass} data-testid="timer-machine-hours" />
              </Field>
              <Field label="Minutter" htmlFor="t-mm">
                <Input id="t-mm" type="number" inputMode="numeric" min={0} max={59} step={15} value={form.machineMinutes} onChange={(e) => set('machineMinutes')(e.target.value)} placeholder="0" disabled={dis} className={inputClass} data-testid="timer-machine-minutes" />
              </Field>
            </div>
          )}
        </div>
      </DisclosureRow>

      {editing && !locked && (
        <Field label="Årsak til endringen" htmlFor="t-reason" hint="Valgfritt. Lagres i historikken sammen med før/etter.">
          <Input id="t-reason" value={form.reason} onChange={(e) => set('reason')(e.target.value)} maxLength={300} placeholder="F.eks. «Ført på feil prosjekt»" className={inputClass} data-testid="timer-reason" />
        </Field>
      )}
    </div>
  );
}

/** Tilbakemelding fra kontoret på en returnert føring – vises til den sendes på nytt. */
export function ReturnNote({ note, className, testId = 'timer-return-note' }) {
  if (!note?.text) return null;
  return (
    <div className={cn('rounded-[10px] border border-[rgba(154,91,0,0.25)] bg-[var(--hr-amber-bg)] px-3 py-2.5', className)} role="status" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[13px] font-semibold text-[var(--hr-amber)]">
        <CornerUpLeft className="h-3.5 w-3.5" aria-hidden="true" /> Tilbakemelding fra kontoret
      </div>
      <p className="mt-0.5 text-[14px] leading-5 text-[var(--hr-text)]">{note.text}</p>
      {note.at && <p className="mt-0.5 text-[12px] text-[var(--hr-muted)]">{formatDateTime(note.at)}{note.by ? ` · ${note.by}` : ''}</p>}
    </div>
  );
}

/**
 * Registreringspanel for timeføring. `entry` = redigering (ellers ny føring fra `initial`).
 * context: 'employee' (demoprofil, ingen medarbeidervelger) | 'office' (medarbeider velges, godkjenn/returner/gjenåpne).
 */
export function TimeEntrySheet({ open, onClose, entry, initial, context = 'employee', actorEmployeeId, today, onChanged }) {
  const { refs, error: refsError, loading: refsLoading, retry: retryRefs } = useDriftRefs();
  const editing = Boolean(entry);
  const status = entry?.status ?? null;
  const locked = status === 'godkjent';
  const [form, setForm] = useState(() => (entry ? timeFormFromEntry(entry) : { ...emptyTimeForm(today, actorEmployeeId), ...(initial ?? {}) }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);

  // Nytt innhold når panelet åpnes for en annen føring / ny registrering.
  const entryKey = entry ? `${entry.id}:${entry.updatedAt}` : `ny:${JSON.stringify(initial ?? {})}:${today}`;
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(entry ? timeFormFromEntry(entry) : { ...emptyTimeForm(today, actorEmployeeId), ...(initial ?? {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entryKey]);

  useEffect(() => {
    if (open && !refs) retryRefs();
  }, [open, refs, retryRefs]);

  const actor = context === 'employee' ? actorEmployeeId : undefined;

  const finish = useCallback(
    (updated, kind) => {
      onChanged?.(updated, kind);
    },
    [onChanged],
  );

  /** Lagre (ny eller redigert). nextStatus: 'utkast' | 'sendt' | undefined (behold). */
  const save = async (nextStatus) => {
    const e = validateTimeForm(form);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error('Sjekk feltene', { description: Object.values(e)[0] });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateTimeEntry(entry.id, { ...timeFormToBody(form), reason: form.reason || null, status: nextStatus && nextStatus !== status ? nextStatus : undefined, actorEmployeeId: actor });
        const added = (updated.history?.length ?? 0) - (entry.history?.length ?? 0);
        if (nextStatus === 'sendt' && status !== 'sendt') toast.success(status === 'returnert' ? 'Sendt på nytt' : 'Sendt inn', { description: `${formatDateShort(form.date)} · ${hoursText(netHours(form))}` });
        else toast.success(added ? 'Endringen er lagret' : 'Ingen endringer å lagre', { description: added ? 'Før/etter ligger i historikken.' : 'Føringen var allerede slik.' });
        finish(updated, 'saved');
      } else {
        const created = await createTimeEntry({ ...timeFormToBody(form), status: nextStatus ?? 'sendt', actorEmployeeId: actor });
        toast.success(nextStatus === 'utkast' ? 'Utkast lagret' : 'Timer sendt inn', { description: `${formatDateShort(form.date)} · ${hoursText(netHours(form))} · ${created.project?.customer ?? ''}` });
        finish(created, 'created');
      }
      onClose();
    } catch (err) {
      toast.error('Kunne ikke lagre', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const transition = async (to, reason) => {
    setSaving(true);
    try {
      const updated = await patchTimeEntry(entry.id, { status: to, reason: reason || undefined, actorEmployeeId: actor });
      const msg = { sendt: status === 'godkjent' ? 'Føringen er gjenåpnet' : 'Sendt inn', utkast: 'Trukket tilbake til utkast', godkjent: 'Godkjent', returnert: 'Returnert til medarbeideren' }[to];
      toast.success(msg);
      finish(updated, 'status');
      if (to === 'godkjent' || to === 'returnert') onClose();
    } catch (err) {
      toast.error('Kunne ikke endre status', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await deleteTimeEntry(entry.id);
      toast.success('Timeføringen er slettet');
      finish(null, 'deleted');
      onClose();
    } catch (err) {
      toast.error('Kunne ikke slette', { description: err.message });
    }
  };

  const title = !editing ? 'Før timer' : locked ? 'Timeføring (godkjent)' : status === 'returnert' ? 'Rett og send på nytt' : 'Rediger timeføring';
  const description = !editing ? 'Dato, tid, kunde og prosjekt først. Tillegg og maskin åpner du ved behov.' : locked ? 'Godkjent og låst. Kontoret kan gjenåpne om noe må rettes.' : 'Endringer lagres med før/etter i historikken.';

  const btn = 'h-12 flex-1 rounded-[12px] text-[16px] font-semibold lg:h-11 lg:text-[14px]';
  const primaryCls = cn(btn, 'bg-[var(--hr-heading)] text-white shadow-none hover:bg-[var(--hr-navy)]');
  const footer = (
    <div className="flex items-center gap-2">
      {editing && !locked && (
        <Button variant="ghost" size="icon" className="h-12 w-12 shrink-0 rounded-[12px] text-[var(--hr-muted)] hover:text-[var(--hr-red)] lg:h-11 lg:w-11" onClick={() => setConfirmDelete(true)} aria-label="Slett timeføring" data-testid="timer-delete">
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
      {!editing && (
        <>
          <Button variant="outline" className={btn} onClick={() => save('utkast')} disabled={saving || !refs} data-testid="timer-save-draft">
            Lagre utkast
          </Button>
          <Button className={primaryCls} onClick={() => save('sendt')} disabled={saving || !refs} data-testid="timer-save">
            {saving ? 'Lagrer …' : 'Send inn'}
          </Button>
        </>
      )}
      {editing && status === 'utkast' && (
        <>
          <Button variant="outline" className={btn} onClick={() => save()} disabled={saving || !refs} data-testid="timer-save-draft">
            Lagre utkast
          </Button>
          <Button className={primaryCls} onClick={() => save('sendt')} disabled={saving || !refs} data-testid="timer-save">
            {saving ? 'Lagrer …' : 'Send inn'}
          </Button>
        </>
      )}
      {editing && status === 'returnert' && (
        <>
          <Button variant="outline" className={btn} onClick={() => save()} disabled={saving || !refs} data-testid="timer-save-draft">
            Lagre
          </Button>
          <Button className={primaryCls} onClick={() => save('sendt')} disabled={saving || !refs} data-testid="timer-save">
            {saving ? 'Lagrer …' : 'Send på nytt'}
          </Button>
        </>
      )}
      {editing && status === 'sendt' && (
        <>
          <Button variant="outline" className={btn} onClick={onClose} disabled={saving} data-testid="timer-cancel">
            Avbryt
          </Button>
          <Button className={primaryCls} onClick={() => save()} disabled={saving || !refs} data-testid="timer-save">
            {saving ? 'Lagrer …' : 'Lagre endringer'}
          </Button>
        </>
      )}
      {editing && locked && (
        <Button className={primaryCls} onClick={onClose} data-testid="timer-cancel">
          Lukk
        </Button>
      )}
    </div>
  );

  return (
    <>
      <FormSheet open={open} onOpenChange={(o) => !o && onClose()} title={title} description={description} testId="timer-form-sheet" footer={footer}>
        {editing && (
          <div className="mb-4 space-y-2.5" data-testid="timer-edit-status">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] bg-[var(--hr-surface-2)] px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <TimeStatusPill status={status} data-testid="timer-sheet-status" />
                {entry.edited && <EditedMark count={entry.history.filter((h) => h.kind === 'endret').length} />}
                {locked && <Lock className="h-3.5 w-3.5 text-[var(--hr-muted-2)]" aria-hidden="true" />}
              </div>
              {context === 'office' ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  {status === 'sendt' && (
                    <>
                      <Button size="sm" variant="outline" className="h-9 rounded-[9px] px-3 text-[13px]" onClick={() => setReturnOpen(true)} disabled={saving} data-testid="timer-office-return">
                        <CornerUpLeft className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Returner …
                      </Button>
                      <Button size="sm" className="h-9 rounded-[9px] bg-[var(--hr-green)] px-3 text-[13px] text-white hover:bg-[var(--hr-green)]/90" onClick={() => transition('godkjent')} disabled={saving} data-testid="timer-office-approve">
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Godkjenn
                      </Button>
                    </>
                  )}
                  {status === 'godkjent' && (
                    <Button size="sm" variant="outline" className="h-9 rounded-[9px] px-3 text-[13px]" onClick={() => transition('sendt', 'Gjenåpnet av kontoret')} disabled={saving} data-testid="timer-office-reopen">
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Gjenåpne
                    </Button>
                  )}
                  {status === 'utkast' && <span className="text-[13px] text-[var(--hr-muted)]">Ikke sendt inn ennå</span>}
                  {status === 'returnert' && <span className="text-[13px] text-[var(--hr-muted)]">Venter på medarbeideren</span>}
                </div>
              ) : (
                <>
                  {status === 'sendt' && (
                    <Button size="sm" variant="ghost" className="h-9 rounded-[9px] px-2.5 text-[13px] text-[var(--hr-muted)] hover:text-[var(--hr-heading)]" onClick={() => transition('utkast')} disabled={saving} data-testid="timer-withdraw">
                      <Undo2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Trekk tilbake
                    </Button>
                  )}
                </>
              )}
            </div>
            <p className="px-0.5 text-[13px] leading-5 text-[var(--hr-muted)]" data-testid="timer-status-help">{TIME_STATUS_HELP[status]}</p>
            <ReturnNote note={entry.returnNote} />
          </div>
        )}
        <RefsGate refs={refs} error={refsError} loading={refsLoading} onRetry={retryRefs} what="kunder, prosjekter og aktiviteter" loadingText="Henter kunder, prosjekter og aktiviteter …">
          {refs && <TimeEntryForm refs={refs} form={form} setForm={setForm} errors={errors} editing={editing} context={context} locked={locked} formKey={entryKey} />}
        </RefsGate>
        {editing && (
          <SheetSection title="Historikk" description="Hver endring og statusovergang lagres med tidspunkt og hvem (demoprofil eller «Kontor (eksempeldata)»). Ikke en autentisert revisjonslogg." className="mt-6">
            <HistoryList history={entry.history} testId="timer-history" />
          </SheetSection>
        )}
      </FormSheet>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette timeføringen?</AlertDialogTitle>
            <AlertDialogDescription>{entry ? `${formatDateShort(entry.date)} · ${entry.project?.customer ?? ''} · ${hoursText(entry.hours)}. Sletting kan ikke angres – vurder å rette føringen i stedet.` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[var(--hr-red)] text-white hover:bg-[var(--hr-red)]/90" data-testid="timer-delete-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReturnReasonDialog open={returnOpen} onOpenChange={setReturnOpen} busy={saving} onConfirm={(reason) => transition('returnert', reason)} />
    </>
  );
}

/** Kontoret returnerer en føring med en kort beskjed til medarbeideren (påkrevd). */
export function ReturnReasonDialog({ open, onOpenChange, onConfirm, busy, entry }) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!open) setReason('');
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[14px] sm:max-w-[440px]" data-testid="timer-return-dialog">
        <DialogHeader>
          <DialogTitle>Returner føringen</DialogTitle>
          <DialogDescription>
            {entry ? `${formatDateShort(entry.date)} · ${entry.employee?.name ?? ''} · ${hoursText(entry.hours)}. ` : ''}Skriv kort hva som må rettes. Beskjeden vises på føringen i Min dag til den sendes på nytt.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={300} placeholder="F.eks. «Maskintimer mangler – legg til timer på gravemaskinen»" className="rounded-[10px] border-[var(--hr-border)] text-[14px] shadow-none" data-testid="timer-return-reason" />
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="h-10 rounded-[10px]" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button className="h-10 rounded-[10px] bg-[var(--hr-heading)] text-white hover:bg-[var(--hr-navy)]" disabled={!reason.trim() || busy} onClick={async () => { await onConfirm(reason.trim()); onOpenChange(false); }} data-testid="timer-return-confirm">
            Returner med beskjed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Tillegg som små merker på kort/rader. */
export function SupplementChips({ list }) {
  if (!list?.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {list.map((s) => (
        <span key={s.id} className="hr-tabular inline-flex whitespace-nowrap rounded-full bg-[var(--hr-surface-2)] px-2 py-0.5 text-[12px] text-[var(--hr-text)]">
          {s.label} {fmtNumber(s.quantity, s.unit === 'stk' ? 0 : 1)} {s.unit}
        </span>
      ))}
    </span>
  );
}

/**
 * Kort for én timeføring (Min dag og mobil kontoroversikt). Kunde først, så prosjekt/aktivitet; timer og status til høyre.
 * Hele hodet åpner føringen; «Kopier» lager en ny, redigerbar føring med samme valg (aldri automatisk innsending).
 */
export function TimeEntryCard({ entry: e, onOpen, onCopy, showEmployee = false, showDate = false, testPrefix = 'time-entry' }) {
  return (
    <li className="group" data-testid={`${testPrefix}-card-${e.id}`}>
      <div className={cn('rounded-[12px] border bg-white transition-colors duration-150', e.status === 'returnert' ? 'border-[rgba(154,91,0,0.35)]' : 'border-[var(--hr-hairline)] hover:border-[var(--hr-border)]')}>
        <button type="button" onClick={() => onOpen(e)} className="flex w-full items-start justify-between gap-3 rounded-t-[12px] px-4 pt-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={`${testPrefix}-open-${e.id}`}>
          <div className="min-w-0">
            <div className="truncate text-[16px] font-semibold leading-6 text-[var(--hr-heading)]" data-testid={`${testPrefix}-customer-${e.id}`}>{e.project?.customer ?? '–'}</div>
            <div className="truncate text-[14px] text-[var(--hr-text)]">
              {e.project?.name}{e.subproject ? ` · ${e.subproject.name}` : ''} · {e.activity?.label}
            </div>
            <div className="hr-tabular mt-1 text-[13px] text-[var(--hr-muted)]">
              {showDate ? `${formatDateShort(e.date)} · ` : ''}{e.fromTime}–{e.toTime}{e.breakMinutes ? ` · pause ${e.breakMinutes} min` : ''}{showEmployee && e.employee ? ` · ${e.employee.name}` : ''}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <span className="hr-num text-[17px] font-semibold leading-6 text-[var(--hr-heading)]">{hoursText(e.hours)}</span>
            <TimeStatusPill status={e.status} data-testid={`${testPrefix}-status-${e.id}`} />
          </div>
        </button>
        {(e.machine || e.supplements?.length > 0 || e.comment || e.edited) && (
          <div className="space-y-1.5 px-4 pt-2 text-[13px]">
            {e.machine && <div className="font-medium text-[var(--hr-accent-text)]">Maskin: {e.machine.internalNo} · {e.machine.name} · {hoursText(e.machineTotalHours)}</div>}
            {(e.supplements?.length > 0 || e.edited) && (
              <div className="flex flex-wrap items-center gap-1.5">
                <SupplementChips list={e.supplements} />
                {e.edited && <EditedMark count={e.history.filter((h) => h.kind === 'endret').length} />}
              </div>
            )}
            {e.comment && <div className="line-clamp-1 text-[var(--hr-muted)]">{e.comment}</div>}
          </div>
        )}
        {e.status === 'returnert' && <ReturnNote note={e.returnNote} className="mx-4 mt-2" testId={`${testPrefix}-return-${e.id}`} />}
        <div className="flex items-center justify-end gap-1 px-2 pb-1.5 pt-1">
          <Button variant="ghost" size="sm" className="h-9 rounded-[8px] px-2.5 text-[13px] text-[var(--hr-muted)] hover:text-[var(--hr-heading)]" onClick={() => onOpen(e)} data-testid={`${testPrefix}-edit-${e.id}`}>
            {e.status === 'returnert' ? 'Rett og send på nytt' : e.status === 'godkjent' ? 'Åpne' : 'Åpne og rett'}
          </Button>
          {onCopy && (
            <Button variant="ghost" size="sm" className="h-9 rounded-[8px] px-2.5 text-[13px] text-[var(--hr-muted)] hover:text-[var(--hr-heading)]" onClick={() => onCopy(e)} data-testid={`${testPrefix}-copy-${e.id}`}>
              Kopier til ny
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
